import { useCallback, useRef, useState } from 'react';
import { type Client, useClient } from 'urql';

import {
  type CompositionV2Filters,
  PAY_V2_QUERY,
  type PositionsEntityKind,
} from '@revelio/data-access';

import {
  buildCustomRoleFilter,
  type ResolvedProfileIds,
} from './useResolveProfileIds';

export interface GeoCompanyEntry {
  rcid: number;
  companyName: string;
  companyLongName: string;
  baseSalary: number;
  upliftDollar: number;
  upliftPercent: number;
}

interface CachedResult {
  companies: GeoCompanyEntry[];
  isLoading: boolean;
}

/**
 * On-demand hook to fetch top companies for a specific geography.
 * Used when hovering over a geo on the map in "explore all" mode.
 * Caches results in a ref to avoid refetching on re-hover.
 */
export const useGeoTopCompanies = (
  resolvedIds: ResolvedProfileIds,
  userSalary: number
): {
  fetchCompanies: (geoId: number, geoEntityType: PositionsEntityKind) => void;
  getCompanies: (geoId: number) => CachedResult;
} => {
  const client = useClient();
  const cacheRef = useRef<Map<number, GeoCompanyEntry[]>>(new Map());
  const loadingRef = useRef<Set<number>>(new Set());
  const [, forceUpdate] = useState(0);

  const fetchCompanies = useCallback(
    (geoId: number, geoEntityType: PositionsEntityKind) => {
      // Already cached or loading
      if (cacheRef.current.has(geoId) || loadingRef.current.has(geoId)) return;
      if (!resolvedIds.roleId) return;

      loadingRef.current.add(geoId);
      forceUpdate((n) => n + 1);

      fetchTopCompaniesForGeo(
        client,
        geoId,
        geoEntityType,
        resolvedIds,
        userSalary
      ).then((companies) => {
        cacheRef.current.set(geoId, companies);
        loadingRef.current.delete(geoId);
        forceUpdate((n) => n + 1);
      });
    },
    [client, resolvedIds, userSalary]
  );

  const getCompanies = useCallback((geoId: number): CachedResult => {
    const cached = cacheRef.current.get(geoId);
    if (cached) return { companies: cached, isLoading: false };
    const loading = loadingRef.current.has(geoId);
    return { companies: [], isLoading: loading };
  }, []);

  return { fetchCompanies, getCompanies };
};

/**
 * Get the last non-null value from a numeric array.
 */
const getLatestValue = (series: ReadonlyArray<number | null>): number => {
  for (let i = series.length - 1; i >= 0; i--) {
    const val = series[i];
    if (val !== null && val !== undefined && val > 0) return val;
  }
  return 0;
};

/**
 * Fetches top companies for a given geography using the PAY_V2_QUERY.
 */
const fetchTopCompaniesForGeo = async (
  client: Client,
  geoId: number,
  geoEntityType: PositionsEntityKind,
  resolvedIds: ResolvedProfileIds,
  userSalary: number
): Promise<GeoCompanyEntry[]> => {
  try {
    const filters: CompositionV2Filters = { raw: false };

    const roleLevel =
      resolvedIds.roleLevels.find((l) => l.type === 'role_k150') ??
      resolvedIds.roleLevels[0];
    if (roleLevel) {
      filters.custom_role = buildCustomRoleFilter(roleLevel);
    }
    if (resolvedIds.seniorityId) {
      filters.seniority = [resolvedIds.seniorityId];
    }

    const result = await client
      .query(PAY_V2_QUERY, {
        req: {
          entities: [{ EntID: geoId, EntType: geoEntityType }],
          filters,
        },
      })
      .toPromise();

    const topCompanies =
      result.data?.pay_v2?.entities?.[0]?.metrics?.top_companies;
    if (!topCompanies) return [];

    return topCompanies
      .map((company) => {
        const baseSalary = getLatestValue(company.base);
        const upliftDollar = baseSalary - userSalary;
        const upliftPercent =
          userSalary > 0 ? (upliftDollar / userSalary) * 100 : 0;

        return {
          rcid: company.index,
          companyName: company.shortName,
          companyLongName: company.longName,
          baseSalary,
          upliftDollar,
          upliftPercent,
        };
      })
      .filter((c) => c.baseSalary > 0)
      .sort((a, b) => b.baseSalary - a.baseSalary)
      .slice(0, 5);
  } catch {
    return [];
  }
};
